import { useEffect } from "react";

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  video: {
    title: string;
    description: string;
    videoUrl: string;
    duration?: string;
  } | null;
}

const VideoModal = ({ isOpen, onClose, video }: VideoModalProps) => {
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !video) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white  max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div
          className={`flex items-center justify-between border-b ${
            video?.title ? "p-6" : "p-1"
          }`}
        >
          <div>
            {video?.title && (
              <h2 className="text-xl font-semibold text-gray-900">
                {video.title}
              </h2>
            )}
            {video?.duration && (
              <p className="text-sm text-gray-600 mt-1">
                Duration: {video.duration}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-black hover:text-gray-600 transition-colors"
          >
            <svg
              className={`${video?.title ? "w-6 h-6" : "w-[18px] h-[18px]"}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className={`${video?.title ? "p-6" : "p-0"}`}>
          <video
            className="w-full h-auto max-h-[60vh]"
            controls
            preload="metadata"
            autoPlay
          >
            <source src={video.videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        {video?.description && (
          <div className="p-6 border-t bg-gray-50">
            <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
            <p className="text-gray-700 leading-relaxed">{video.description}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoModal;
